<script setup>
import { NCard, NButton, NTag, NIcon } from 'naive-ui';
import { useRouter } from 'vue-router';
import { dt } from '@/utils/i18n';
import { currentUser } from '@/services/auth';

const router = useRouter();

const handleSearchProviders = () => {
  router.push('/validator-search');
};
</script>

<template>
  <div class="validator-profile-container">
    <div class="profile-wrapper">
      <NCard class="validator-card">
        <template #header>
          <div class="header-content">
            <div class="validator-avatar">
              <span class="avatar-icon">⚖️</span>
            </div>
            <div class="validator-info">
              <h1 class="validator-title">{{ dt('validator.welcome') || 'Validator Dashboard' }}</h1>
              <p class="validator-subtitle">{{ currentUser?.email || 'Validator' }}</p>
              <NTag type="success" size="small" class="role-tag">
                ✓ Validator Access
              </NTag>
            </div>
          </div>
        </template>

        <div class="validator-content">
          <div class="description-section">
            <h3 class="section-title">{{ dt('validator.yourRole') || 'Your Role' }}</h3>
            <p class="description-text">
              {{ dt('validator.description') || 'As a validator, you help maintain data quality by reviewing and verifying provider information. Your work ensures users get accurate, up-to-date healthcare provider details.' }}
            </p>
          </div>

          <div class="tasks-section">
            <h3 class="section-title">{{ dt('validator.tasks') || 'Your Tasks' }}</h3>
            <div class="task-list">
              <div class="task-item">
                <span class="task-icon">📋</span>
                <span class="task-text">{{ dt('validator.task1') || 'Review raw provider data for accuracy' }}</span>
              </div>
              <div class="task-item">
                <span class="task-icon">✅</span>
                <span class="task-text">{{ dt('validator.task2') || 'Verify provider information and mark as validated' }}</span>
              </div>
              <div class="task-item">
                <span class="task-icon">🔍</span>
                <span class="task-text">{{ dt('validator.task3') || 'Search and filter providers by status and source' }}</span>
              </div>
            </div>
          </div>

          <div class="action-section">
            <NButton 
              type="primary" 
              size="large"
              class="search-button"
              @click="handleSearchProviders"
            >
              <span class="button-icon">🔍</span>
              {{ dt('validator.searchProviders') || 'Search Providers' }}
            </NButton>
            <p class="action-hint">
              {{ dt('validator.searchHint') || 'Click to start reviewing and validating provider information' }}
            </p>
          </div>
        </div>
      </NCard>
    </div>
  </div>
</template>

<style scoped>
.validator-profile-container {
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 24px 16px;
}

.profile-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.validator-card {
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.validator-avatar {
  width: 64px;
  height: 64px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-icon {
  font-size: 28px;
  filter: grayscale(1) brightness(10);
}

.validator-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.validator-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.validator-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.role-tag {
  align-self: flex-start;
}

.validator-content {
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 12px 0;
}

.description-text {
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.6;
  margin: 0;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.task-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.task-text {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
}

.action-section {
  text-align: center;
  padding: 24px 0;
}

.search-button {
  font-size: 1rem;
  padding: 12px 32px;
  height: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.button-icon {
  font-size: 1.125rem;
}

.action-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 12px 0 0 0;
  font-style: italic;
}

@media (max-width: 600px) {
  .validator-profile-container {
    padding: 16px 8px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .validator-info {
    align-items: center;
  }
  
  .validator-title {
    font-size: 1.25rem;
  }
  
  .search-button {
    width: 100%;
    justify-content: center;
  }
}
</style>